import { ObjectTools } from '../../tools/object-tools';

export type SolLayoutType = 'vertical' | 'horizontal' | 'grid';

export type SolLayoutGrid =
  | 'default'
  | '100'
  | '50 50'
  | '33 33 33'
  | '25 25 25 25'
  | '25 25 50'
  | '50 25 25'
  | '25 50 25'
  | '75 25'
  | '25 75'
  | '33 66'
  | '66 33'
  | 'min max'
  | 'max min';

export type SolLayoutPadding = '0' | '4' | '8' | '16' | '32' | '64';

export type SolLayoutGap = '0' | '4' | '8' | '16' | '32';

export type SolLayoutAlignX = 'default' | 'left' | 'center' | 'right' | 'space-between';

export type SolLayoutAlignY = 'default' | 'top' | 'center' | 'bottom';

export type SolLayoutGrow = 'default' | 'grow';

export type SolLayoutWrap = 'default' | 'wrap';

export type SolLayoutMinWidth =
  'default' | '32' | '40' | '48' | '56' | '64' | '72' | '80' | '96' | '112' | '128' | '160' | '192' | '256';

export interface SolLayoutHostStyle {
  gridTemplateColumns?: string;
}

/**
 * Immutable description of a SOL layout, mirroring the configurable inputs of the layout component.
 */
export class SolLayout {
  private static readonly defaultLayout = new SolLayout(
    'vertical',
    'default',
    '16',
    '0',
    '0',
    '0',
    '0',
    'default',
    'default',
    'default',
    'default',
    'default',
    'default',
    '',
    {},
  )
    .calculateHostClass()
    .calculateHostStyle();

  private constructor(
    public readonly type: SolLayoutType,
    public readonly grid: SolLayoutGrid,
    public readonly gap: SolLayoutGap,
    public readonly pl: SolLayoutPadding,
    public readonly pr: SolLayoutPadding,
    public readonly pt: SolLayoutPadding,
    public readonly pb: SolLayoutPadding,
    public readonly alignX: SolLayoutAlignX,
    public readonly alignY: SolLayoutAlignY,
    public readonly growX: SolLayoutGrow,
    public readonly growY: SolLayoutGrow,
    public readonly wrap: SolLayoutWrap,
    public readonly minWidth: SolLayoutMinWidth,
    public readonly hostClass: string,
    public readonly hostStyle: SolLayoutHostStyle,
  ) {}

  public static of(): SolLayout {
    return SolLayout.defaultLayout;
  }

  public withType(type: SolLayoutType): SolLayout {
    if (type === this.type) {
      return this;
    }
    return this.copy({ type }).calculateHostClass().calculateHostStyle();
  }

  public withGrid(grid: SolLayoutGrid): SolLayout {
    if (grid === this.grid) {
      return this;
    }
    return this.copy({ grid }).calculateHostStyle();
  }

  public withGap(gap: SolLayoutGap): SolLayout {
    if (gap === this.gap) {
      return this;
    }
    return this.copy({ gap }).calculateHostClass().calculateHostStyle();
  }

  public withPl(pl: SolLayoutPadding): SolLayout {
    if (pl === this.pl) {
      return this;
    }
    return this.copy({ pl }).calculateHostClass();
  }

  public withPr(pr: SolLayoutPadding): SolLayout {
    if (pr === this.pr) {
      return this;
    }
    return this.copy({ pr }).calculateHostClass();
  }

  public withPt(pt: SolLayoutPadding): SolLayout {
    if (pt === this.pt) {
      return this;
    }
    return this.copy({ pt }).calculateHostClass();
  }

  public withPb(pb: SolLayoutPadding): SolLayout {
    if (pb === this.pb) {
      return this;
    }
    return this.copy({ pb }).calculateHostClass();
  }

  public withP(padding: SolLayoutPadding): SolLayout {
    if (padding === this.pl && padding === this.pr && padding === this.pt && padding === this.pb) {
      return this;
    }
    return this.copy({ pl: padding, pr: padding, pt: padding, pb: padding }).calculateHostClass();
  }

  public withPx(padding: SolLayoutPadding): SolLayout {
    if (padding === this.pl && padding === this.pr) {
      return this;
    }
    return this.copy({ pl: padding, pr: padding }).calculateHostClass();
  }

  public withPy(padding: SolLayoutPadding): SolLayout {
    if (padding === this.pt && padding === this.pb) {
      return this;
    }
    return this.copy({ pt: padding, pb: padding }).calculateHostClass();
  }

  public withAlignX(alignX: SolLayoutAlignX): SolLayout {
    if (alignX === this.alignX) {
      return this;
    }
    return this.copy({ alignX }).calculateHostClass();
  }

  public withAlignY(alignY: SolLayoutAlignY): SolLayout {
    if (alignY === this.alignY) {
      return this;
    }
    return this.copy({ alignY }).calculateHostClass();
  }

  public withGrowX(growX: SolLayoutGrow): SolLayout {
    if (growX === this.growX) {
      return this;
    }
    return this.copy({ growX }).calculateHostClass();
  }

  public withGrowY(growY: SolLayoutGrow): SolLayout {
    if (growY === this.growY) {
      return this;
    }
    return this.copy({ growY }).calculateHostClass();
  }

  public withGrow(grow: SolLayoutGrow): SolLayout {
    if (grow === this.growX && grow === this.growY) {
      return this;
    }
    return this.copy({ growX: grow, growY: grow }).calculateHostClass();
  }

  public withWrap(wrap: SolLayoutWrap): SolLayout {
    if (wrap === this.wrap) {
      return this;
    }
    return this.copy({ wrap }).calculateHostClass();
  }

  /**
   * Gives the layout a minimum width it does not shrink below, and makes its children fill it, so a
   * column of tags or dates in a list lines up whatever each one's own width.
   */
  public withMinWidth(minWidth: SolLayoutMinWidth): SolLayout {
    if (minWidth === this.minWidth) {
      return this;
    }
    return this.copy({ minWidth }).calculateHostClass();
  }

  private calculateHostClass(): SolLayout {
    let hostClass = SolLayout.toTypeClass(this.type);
    hostClass += SolLayout.toGapClasses(this.gap);
    hostClass += SolLayout.toPaddingClasses(this.pl, this.pr, this.pt, this.pb);
    hostClass += SolLayout.toAlignClasses(this.type, this.alignX, this.alignY);
    hostClass += SolLayout.toGrowClasses(this.growX, this.growY);
    hostClass += SolLayout.toWrapClasses(this.wrap);
    hostClass += SolLayout.toMinWidthClasses(this.minWidth);
    return this.copy({ hostClass });
  }

  private calculateHostStyle(): SolLayout {
    const hostStyle = SolLayout.toHostStyle(this.grid, this.gap);
    return this.copy({ hostStyle });
  }

  private static toTypeClass(type: SolLayoutType): string {
    switch (type) {
      case 'vertical':
        return 'sol-row';
      case 'horizontal':
        return 'sol-columns';
      case 'grid':
        return `sol-grid`;
    }
  }

  /**
   * Normalizes the grid to include extra space for gaps to the largest item (see '+').
   *
   *             | 1   2   3   4   5   6   7   8   9   10  11  12
   *             | -   -   -   -   -   -   -   -   -   -   -   -
   * 50 50       | ----------------------- -----------------------
   * 25 75       | ----------- ---------------------------------++
   * 75 25       | ---------------------------------++ -----------
   * 25 25 50    | ----------- ----------- ----------------------+
   * 50 25 25    | ----------------------+ ----------- -----------
   * 25 50 25    | ----------- ----------------------+ -----------
   * 25 25 25 25 | ----------- ----------- ----------- -----------
   *             | 1   2   3   4   5   6   7   8   9   10  11  12
   *             | -   -   -   -   -   -   -   -   -   -   -   -
   * 33 66       | --------------- ------------------------------+
   * 66 33       | ------------------------------+ ---------------
   * 33 33 33    | --------------- --------------- ---------------
   */
  private static toHostStyle(grid: SolLayoutGrid, gap: SolLayoutGap): SolLayoutHostStyle {
    switch (grid) {
      case 'default':
        return {};
      case '100':
        return { gridTemplateColumns: '1fr' };
      case 'min max':
        return { gridTemplateColumns: 'min-content 1fr' };
      case 'max min':
        return { gridTemplateColumns: '1fr min-content' };
      default:
        const parts = grid.split(' ').map(part => parseInt(part));
        const gapPx = parseInt(gap);
        let gridTemplateColumns = '';
        for (const part of parts) {
          const percentage = part === 33 ? '33.333' : part === 66 ? '66.666' : part;
          const addedPx = 0 - gapPx * (1 - part / 100);
          gridTemplateColumns += `calc(${percentage}% + ${addedPx}px)`;
        }
        return { gridTemplateColumns };
    }
  }

  private static toGapClasses(gap: SolLayoutGap): string {
    switch (gap) {
      case '0':
        return '';
      case '4':
        return ' sol-g-4';
      case '8':
        return ' sol-g-8';
      case '16':
        return ' sol-g-16';
      case '32':
        return ' sol-g-32';
    }
  }

  private static toPaddingClasses(
    pl: SolLayoutPadding,
    pr: SolLayoutPadding,
    pt: SolLayoutPadding,
    pb: SolLayoutPadding,
  ): string {
    // pl
    let plClass = '';
    switch (pl) {
      case '0':
        plClass = '';
        break;
      case '4':
        plClass = ' sol-pl-4';
        break;
      case '8':
        plClass = ' sol-pl-8';
        break;
      case '16':
        plClass = ' sol-pl-16';
        break;
      case '32':
        plClass = ' sol-pl-32';
        break;
      case '64':
        plClass = ' sol-pl-64';
        break;
    }

    // pr
    let prClass = '';
    switch (pr) {
      case '0':
        prClass = '';
        break;
      case '4':
        prClass = ' sol-pr-4';
        break;
      case '8':
        prClass = ' sol-pr-8';
        break;
      case '16':
        prClass = ' sol-pr-16';
        break;
      case '32':
        prClass = ' sol-pr-32';
        break;
      case '64':
        prClass = ' sol-pr-64';
        break;
    }

    // pt
    let ptClass = '';
    switch (pt) {
      case '0':
        ptClass = '';
        break;
      case '4':
        ptClass = ' sol-pt-4';
        break;
      case '8':
        ptClass = ' sol-pt-8';
        break;
      case '16':
        ptClass = ' sol-pt-16';
        break;
      case '32':
        ptClass = ' sol-pt-32';
        break;
      case '64':
        ptClass = ' sol-pt-64';
        break;
    }

    // pb
    let pbClass = '';
    switch (pb) {
      case '0':
        pbClass = '';
        break;
      case '4':
        pbClass = ' sol-pb-4';
        break;
      case '8':
        pbClass = ' sol-pb-8';
        break;
      case '16':
        pbClass = ' sol-pb-16';
        break;
      case '32':
        pbClass = ' sol-pb-32';
        break;
      case '64':
        pbClass = ' sol-pb-64';
        break;
    }

    return plClass + prClass + ptClass + pbClass;
  }

  private static toAlignClasses(type: SolLayoutType, alignX: SolLayoutAlignX, alignY: SolLayoutAlignY): string {
    // alignX
    let alignXClass = '';
    const isRows = type === 'vertical';
    switch (alignX) {
      case 'default':
        alignXClass = isRows ? '' : ' sol-stretch';
        break;
      case 'left':
        alignXClass = !isRows ? ' sol-justify-left' : ' sol-align-start';
        break;
      case 'center':
        alignXClass = !isRows ? ' sol-justify-center' : ' sol-align-center';
        break;
      case 'right':
        alignXClass = !isRows ? ' sol-justify-right' : ' sol-align-end';
        break;
      case 'space-between':
        alignXClass = !isRows ? ' sol-justify-space-between' : '';
        break;
    }

    // alignY
    let alignYClass = '';
    switch (alignY) {
      case 'default':
        alignYClass = '';
        break;
      case 'top':
        alignYClass = !isRows ? ' sol-align-start' : ' sol-justify-left';
        break;
      case 'center':
        alignYClass = !isRows ? ' sol-align-center' : ' sol-justify-center';
        break;
      case 'bottom':
        alignYClass = !isRows ? ' sol-align-end' : ' sol-justify-right';
        break;
    }
    return alignXClass + alignYClass;
  }

  private static toGrowClasses(growX: SolLayoutGrow, growY: SolLayoutGrow): string {
    let growXClass = '';
    switch (growX) {
      case 'default':
        growXClass = '';
        break;
      case 'grow':
        growXClass = ' sol-grow-x';
        break;
    }

    let growYClass = '';
    switch (growY) {
      case 'default':
        growYClass = '';
        break;
      case 'grow':
        growYClass = ' sol-grow-y';
        break;
    }
    return growXClass + growYClass;
  }

  private static toWrapClasses(wrap: SolLayoutWrap): string {
    switch (wrap) {
      case 'default':
        return '';
      case 'wrap':
        return ' sol-wrap';
    }
  }

  private static toMinWidthClasses(minWidth: SolLayoutMinWidth): string {
    switch (minWidth) {
      case 'default':
        return '';
      default:
        return ` sol-min-w-${minWidth} sol-fill`;
    }
  }

  private copy(props: Partial<SolLayout>): SolLayout {
    return ObjectTools.copy<SolLayout>(this, props);
  }
}
